import { useChatActions, useChatStatus } from '@ai-sdk-tools/store';
import { useApolloClient } from '@apollo/client';
import { AppTypeEnum } from '@newfront-insurance/ai-api';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';

import type { ChatInputContextValue } from './chat-input-context';
import { getOptimisticChatAccountName } from './optimistic-chat';
import { useScrollToBottom } from '../../../../hooks/use-scroll-to-bottom';
import type { UIMessageWithMetadata } from '../../../../lib/types';
import { GET_CHAT_WITH_MESSAGES, GET_CHATS_BY_FILTERS } from '../../../../queries/chats';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { useSkillsSafe } from '../../contexts/skills-context';
import { useNovaContext } from '../../hooks/use-chat-context';
import { getCountBucket, getLengthBucket, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';
import { markChatStreamActive, normalizeChatUuid } from '../../lib/notification-helpers';

/**
 * Hook that sets up all chat input logic and returns the context value.
 * This hook focuses on core input functionality (text input, submit).
 * Toolbar components (like ChatAttachments) contribute additional context
 * via ChatOverlayContext.additionalContext.
 *
 * @example
 * ```tsx
 * function MyCustomInput() {
 *   const contextValue = useChatInputSetup();
 *
 *   return (
 *     <ChatInputProvider value={contextValue}>
 *       <ChatInput>
 *         <ChatInputTextarea />
 *         <ChatInputToolbar>
 *           <ChatAttachments /> // Sets additionalContext via ChatOverlayContext
 *           <SendButton />
 *         </ChatInputToolbar>
 *       </ChatInput>
 *     </ChatInputProvider>
 *   );
 * }
 * ```
 */
export function useChatInputSetup(): ChatInputContextValue {
  const { sendMessage } = useChatActions<UIMessageWithMetadata>();
  const status = useChatStatus();
  const { accountUuid, classUuid } = useNovaContext();
  const apolloClient = useApolloClient();
  const {
    additionalContext,
    chatUuid,
    userUuid,
    accountName,
    displayAccount,
    hasAnyMessages,
    isVisible,
    isStandaloneMode,
    isPublicMode,
    notificationController,
    appType,
    effectiveAppType,
    setAccountUuid,
    queuedNewChatInput,
    clearQueuedNewChatInput,
    setRetryLastRequest,
  } = useChatOverlayContext();

  const analytics = useAnalytics();
  const skillsContext = useSkillsSafe();
  const [input, setInput] = useState('');
  const editorRef = useRef<HTMLTextAreaElement | HTMLDivElement>(null);
  const writtenChatUuidsRef = useRef<Set<string>>(new Set());
  // Tracks whether we've already fired the "first typed" event for the current message.
  // Reset after each submit so we track once per message.
  const hasTrackedFirstTypedRef = useRef(false);

  const resetHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = '92px';
    }
  }, []);

  const trackingSetInput = useCallback(
    (value: string | ((prev: string) => string)) => {
      setInput((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        if (!hasTrackedFirstTypedRef.current && prev === '' && next !== '') {
          const attachmentCount = Array.isArray(additionalContext?.attachmentFileUuids)
            ? additionalContext.attachmentFileUuids.length
            : 0;
          analytics.track({
            event: 'Nova Chat Input First Typed',
            properties: { hasAttachments: attachmentCount > 0, attachmentCount },
          });
          hasTrackedFirstTypedRef.current = true;
        }
        return next;
      });
    },
    [additionalContext?.attachmentFileUuids, analytics],
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>, manualInput?: string) => {
      e?.preventDefault();

      const { messageMetadata, ...payloadAdditionalContext } = additionalContext ?? {};
      const metadataFiles =
        messageMetadata &&
        typeof messageMetadata === 'object' &&
        'files' in messageMetadata &&
        Array.isArray(messageMetadata.files)
          ? messageMetadata.files
          : [];
      const inferredAttachmentFileUuids = metadataFiles
        .map((file) => (file && typeof file === 'object' && 'uuid' in file ? file.uuid : undefined))
        .filter((uuid): uuid is string => typeof uuid === 'string');
      const explicitAttachmentFileUuids = Array.isArray(payloadAdditionalContext.attachmentFileUuids)
        ? payloadAdditionalContext.attachmentFileUuids.filter((uuid): uuid is string => typeof uuid === 'string')
        : [];
      const normalizedAttachmentFileUuids =
        explicitAttachmentFileUuids.length > 0 ? explicitAttachmentFileUuids : inferredAttachmentFileUuids;
      const normalizedAdditionalContext = {
        ...payloadAdditionalContext,
        ...(normalizedAttachmentFileUuids.length > 0 ? { attachmentFileUuids: normalizedAttachmentFileUuids } : {}),
      };

      const resolvedContext = await resolveContextValues(normalizedAdditionalContext);

      const bodyData = {
        accountUuid,
        ...(classUuid && { classUuid }),
        ...(resolvedContext &&
          Object.keys(resolvedContext).length > 0 && {
            ...resolvedContext,
            additionalContext: resolvedContext,
          }),
      };
      const messageText = manualInput || input.trim();

      if (messageText) {
        const metadata: UIMessageWithMetadata['metadata'] = {
          files: [],
          ...(messageMetadata && typeof messageMetadata === 'object' ? messageMetadata : {}),
        };

        if (chatUuid.startsWith('new-chat-') && !hasAnyMessages) {
          setAccountUuid(accountUuid);
        }

        const attachmentCount = normalizedAttachmentFileUuids.length;
        const firstSkillCommandHandle = messageText.match(/^\/([\w-]+)(?:\s|$)/)?.[1];
        const submittedSkill = firstSkillCommandHandle
          ? skillsContext?.findSkill({ command: firstSkillCommandHandle })
          : undefined;
        analytics.track({
          event: 'Nova Chat Message Submitted',
          properties: {
            hasAttachments: attachmentCount > 0,
            attachmentCount,
            attachmentFileUuids: normalizedAttachmentFileUuids,
            chatUuid,
            messageLengthBucket: getLengthBucket(messageText.length),
          },
        });

        if (submittedSkill) {
          trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_COMMAND_SUBMITTED, {
            attachmentCountBucket: getCountBucket(attachmentCount),
            attachmentFileUuids: normalizedAttachmentFileUuids,
            category: submittedSkill.category,
            chatUuid,
            commandHandle: submittedSkill.command,
            commandId: submittedSkill.id,
            commandKind: 'skill',
            hasAttachments: attachmentCount > 0,
            messageLengthBucket: getLengthBucket(messageText.length),
            skillId: submittedSkill.id,
            skillSource: submittedSkill.source,
          });

          trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_INVOCATION_SUBMITTED, {
            attachmentCountBucket: getCountBucket(attachmentCount),
            attachmentFileUuids: normalizedAttachmentFileUuids,
            category: submittedSkill.category,
            chatUuid,
            commandHandle: submittedSkill.command,
            commandId: submittedSkill.id,
            commandKind: 'skill',
            hasAttachments: attachmentCount > 0,
            messageLengthBucket: getLengthBucket(messageText.length),
            skillId: submittedSkill.id,
            skillSource: submittedSkill.source,
          });
        }

        const messageId = v4();
        const userMessage = {
          id: messageId,
          role: 'user' as const,
          parts: [{ type: 'text' as const, text: messageText }],
          metadata,
        };
        const requestOptions = { body: bodyData };

        hasTrackedFirstTypedRef.current = false;
        setRetryLastRequest(() => sendMessage({ ...userMessage, messageId }, requestOptions));

        sendMessage(userMessage, requestOptions);
        if (isPublicMode) {
          setInput('');
          resetHeight();
          return;
        }

        notificationController.registerStream(chatUuid, isVisible, isStandaloneMode);

        const currentChats = apolloClient.readQuery({
          query: GET_CHATS_BY_FILTERS,
          variables: {
            userUuid,
            appType: effectiveAppType,
          },
        });
        const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);

        if (chatUuid.startsWith('new-chat-')) {
          // Check if we've already written this chat to the cache to prevent duplicates
          if (!writtenChatUuidsRef.current.has(chatUuid)) {
            const chatAlreadyExists = currentChats?.chatsList?.some(
              (chat) => normalizeChatUuid(chat.uuid ?? '') === chatUuidWithoutPrefix,
            );

            if (!chatAlreadyExists && !isPublicMode) {
              const optimisticTimestamp = new Date().toISOString();
              const optimisticStreamId = `pending-stream-${chatUuidWithoutPrefix}`;

              apolloClient.writeQuery({
                query: GET_CHATS_BY_FILTERS,
                variables: {
                  userUuid,
                  appType: effectiveAppType,
                },
                data: {
                  chatsList: [
                    {
                      // Keep the optimistic sidebar row prefixed so selecting it still skips
                      // persisted-chat reads until the stream has created the backend chat.
                      uuid: chatUuid,
                      userUuid,
                      accountUuid: accountUuid ?? null,
                      appType: appType ?? AppTypeEnum.NAVIGATOR,
                      title: 'New Chat',
                      summary: 'New Chat',
                      createdAt: optimisticTimestamp,
                      updatedAt: optimisticTimestamp,
                      activeStreamId: optimisticStreamId,
                      lastMessageAt: optimisticTimestamp,
                      hasUnreadMessages: false,
                    },
                    ...(currentChats?.chatsList ?? []),
                  ],
                },
              });

              // Mark this chat UUID as written to prevent duplicates
              writtenChatUuidsRef.current.add(chatUuid);

              apolloClient.writeQuery({
                query: GET_CHAT_WITH_MESSAGES,
                variables: { uuid: chatUuid },
                data: {
                  getChat: {
                    userUuid,
                    accountUuid: accountUuid ?? null,
                    appType: appType ?? AppTypeEnum.NAVIGATOR,
                    account: {
                      uuid: accountUuid ?? null,
                      name: getOptimisticChatAccountName({
                        displayAccountName: displayAccount?.name,
                        accountName,
                      }),
                    },
                    title: 'New Chat',
                    summary: 'New Chat',
                    uuid: chatUuid,
                    messages: [],
                    activeStreamId: optimisticStreamId,
                    hasUnreadMessages: false,
                    createdAt: optimisticTimestamp,
                    updatedAt: optimisticTimestamp,
                  },
                },
              });
            }
          }
        } else {
          markChatStreamActive({
            apolloClient,
            chatUuid,
            userUuid,
            appType: effectiveAppType,
          });
        }

        setInput('');
        resetHeight();
      }
    },
    [
      input,
      additionalContext,
      sendMessage,
      accountUuid,
      classUuid,
      resetHeight,
      chatUuid,
      userUuid,
      accountName,
      displayAccount?.name,
      hasAnyMessages,
      apolloClient,
      isVisible,
      isStandaloneMode,
      notificationController,
      appType,
      effectiveAppType,
      isPublicMode,
      setAccountUuid,
      setRetryLastRequest,
      analytics,
      skillsContext,
    ],
  );

  useEffect(() => {
    if (!queuedNewChatInput) {
      return;
    }

    setInput(queuedNewChatInput);
    hasTrackedFirstTypedRef.current = false;
    clearQueuedNewChatInput();

    window.setTimeout(() => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }

      editor.focus();
      editor.style.height = 'auto';
      editor.style.height = `${editor.scrollHeight + 2}px`;

      if (editor instanceof HTMLTextAreaElement) {
        editor.setSelectionRange(queuedNewChatInput.length, queuedNewChatInput.length);
        return;
      }

      if (editor instanceof HTMLDivElement) {
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, 0);
  }, [clearQueuedNewChatInput, queuedNewChatInput]);

  const { isAtBottom, scrollToBottom } = useScrollToBottom();

  return useMemo(
    () => ({
      input,
      setInput: trackingSetInput,
      editorRef: editorRef as React.RefObject<HTMLTextAreaElement | HTMLDivElement>,
      handleSubmit,
      status,
      isAtBottom,
      scrollToBottom,
      isPublicMode,
    }),
    [input, editorRef, handleSubmit, status, isAtBottom, scrollToBottom, isPublicMode, trackingSetInput],
  );
}

/**
 * Resolves any function values in a context object by calling them (supports async).
 * This allows components like OutlookChatPayload to store getter functions
 * (e.g. token getters) that are resolved fresh at send time instead of caching
 * values that can become stale.
 */
async function resolveContextValues(context: Record<string, unknown>): Promise<Record<string, unknown>> {
  const entries = Object.entries(context);
  const resolved = await Promise.all(
    entries.map(async ([key, value]) => [key, typeof value === 'function' ? await value() : value]),
  );
  return Object.fromEntries(resolved);
}
